import { useState } from 'react';
import { toast } from '../components/Toast';
import { crmApi } from '../services/crmApi';
import type { ComprehensiveAnalysis, OptimizationReport, PurchaseListReport, CourseRecommendations, CourseCertificate } from '../types/crm';

interface AIDeps {
  id: string | undefined;
  loadApplication: () => Promise<void>;
}

export function useApplicationAI({ id, loadApplication }: AIDeps) {
  // Comprehensive AI analysis
  const [generatingAI, setGeneratingAI] = useState(false);
  const [comprehensiveAnalysis, setComprehensiveAnalysis] = useState<ComprehensiveAnalysis | null>(null);
  const [showComprehensiveAnalysis, setShowComprehensiveAnalysis] = useState(false);

  // Optimization report
  const [optimizationReport, setOptimizationReport] = useState<OptimizationReport | null>(null);
  const [showOptimizationReport, setShowOptimizationReport] = useState(false);
  const [generatingOptimization, setGeneratingOptimization] = useState(false);

  // Purchase list
  const [purchaseListReport, setPurchaseListReport] = useState<PurchaseListReport | null>(null);
  const [showPurchaseList, setShowPurchaseList] = useState(false);
  const [generatingPurchaseList, setGeneratingPurchaseList] = useState(false);

  // Course recommendations
  const [courseRecommendations, setCourseRecommendations] = useState<CourseRecommendations | null>(null);
  const [showCourseRecommendations, setShowCourseRecommendations] = useState(false);
  const [generatingCourseRecommendations, setGeneratingCourseRecommendations] = useState(false);
  const [generatedCourseCertificate, setGeneratedCourseCertificate] = useState<CourseCertificate | null>(null);
  const [showCourseCertificate, setShowCourseCertificate] = useState(false);

  // Business profile AI
  const [generatingBusinessProfile, setGeneratingBusinessProfile] = useState(false);
  const [aiBusinessProfile, setAiBusinessProfile] = useState<string | null>(null);
  const [showBusinessProfileAI, setShowBusinessProfileAI] = useState(false);

  // Product analysis (non-PUP)
  const [productAnalysis, setProductAnalysis] = useState<any>(null);
  const [showProductAnalysis, setShowProductAnalysis] = useState(false);

  // Product AI action result
  const [productActionResult, setProductActionResult] = useState<any>(null);
  const [showProductActionResult, setShowProductActionResult] = useState(false);
  const [generatingProductAction, setGeneratingProductAction] = useState(false);

  // Financial forecast
  const [financialForecast, setFinancialForecast] = useState<any>(null);
  const [showFinancialForecast, setShowFinancialForecast] = useState(false);
  const [generatingForecast, setGeneratingForecast] = useState(false);

  // Tax advisor
  const [taxAdvice, setTaxAdvice] = useState<any>(null);
  const [showTaxAdvice, setShowTaxAdvice] = useState(false);
  const [generatingTaxAdvice, setGeneratingTaxAdvice] = useState(false);

  // Market research
  const [marketResearch, setMarketResearch] = useState<any>(null);
  const [showMarketResearch, setShowMarketResearch] = useState(false);
  const [generatingMarketResearch, setGeneratingMarketResearch] = useState(false);

  const handleGenerateAI = async () => {
    if (!id) return;
    setGeneratingAI(true);
    try {
      const analysis = await crmApi.generateAIAnalysis(parseInt(id));
      await loadApplication();
      setComprehensiveAnalysis(analysis);
      setShowComprehensiveAnalysis(true);
    } catch (error) {
      console.error('Error generating AI analysis:', error);
      toast.error('Błąd podczas generowania analizy AI');
    } finally {
      setGeneratingAI(false);
    }
  };

  const handleGenerateOptimization = async () => {
    if (!id) return;
    setGeneratingOptimization(true);
    try {
      const report = await crmApi.generateOptimizationReport(parseInt(id));
      setOptimizationReport(report);
      setShowOptimizationReport(true);
    } catch (error) {
      console.error('Error generating optimization report:', error);
      toast.error('Błąd podczas generowania raportu optymalizacji');
    } finally {
      setGeneratingOptimization(false);
    }
  };

  const handleGeneratePurchaseList = async () => {
    if (!id) return;
    setGeneratingPurchaseList(true);
    try {
      const report = await crmApi.generatePurchaseList(parseInt(id));
      setPurchaseListReport(report);
      setShowPurchaseList(true);
    } catch (error) {
      console.error('Error generating purchase list:', error);
      toast.error('Błąd podczas generowania listy zakupów');
    } finally {
      setGeneratingPurchaseList(false);
    }
  };

  const handleRecommendCourses = async () => {
    if (!id) return;
    setGeneratingCourseRecommendations(true);
    try {
      const recommendations = await crmApi.recommendCourses(parseInt(id));
      setCourseRecommendations(recommendations);
      setShowCourseRecommendations(true);
    } catch (error) {
      console.error('Error recommending courses:', error);
      toast.error('Błąd podczas generowania rekomendacji kursów');
    } finally {
      setGeneratingCourseRecommendations(false);
    }
  };

  const handleGenerateCourseCertificate = async (courseId: string) => {
    if (!id) return;
    try {
      const certificate = await crmApi.generateCourseCertificate(parseInt(id), courseId);
      setGeneratedCourseCertificate(certificate);
      setShowCourseCertificate(true);
      toast.info(`Certyfikat wygenerowany: ${certificate.certificateNumber}`);
    } catch (error) {
      console.error('Error generating course certificate:', error);
      toast.error('Błąd podczas generowania certyfikatu kursu');
    }
  };

  const handleGenerateProductAnalysis = async () => {
    if (!id) return;
    setGeneratingAI(true);
    try {
      const result = await crmApi.generateProductAnalysis(parseInt(id));
      setProductAnalysis(result.analysis);
      setShowProductAnalysis(true);
    } catch (error) {
      console.error('Error generating product analysis:', error);
      toast.error('Błąd podczas generowania analizy AI');
    } finally {
      setGeneratingAI(false);
    }
  };

  const handleProductAIAction = async (actionType: string) => {
    if (!id) return;
    setGeneratingProductAction(true);
    try {
      const result = await crmApi.runProductAIAction(parseInt(id), actionType);
      setProductActionResult(result.result);
      setShowProductActionResult(true);
    } catch (error) {
      console.error('Error running product AI action:', error);
      toast.error('Błąd podczas wykonywania akcji AI');
    } finally {
      setGeneratingProductAction(false);
    }
  };

  const handleGenerateFinancialForecast = async () => {
    if (!id) return;
    setGeneratingForecast(true);
    try {
      const forecast = await crmApi.generateFinancialForecast(parseInt(id));
      setFinancialForecast(forecast);
      setShowFinancialForecast(true);
    } catch (error) {
      console.error('Error generating financial forecast:', error);
      toast.error('Błąd podczas generowania prognozy finansowej');
    } finally {
      setGeneratingForecast(false);
    }
  };

  const handleGenerateTaxAdvice = async () => {
    if (!id) return;
    setGeneratingTaxAdvice(true);
    try {
      const advice = await crmApi.generateTaxAdvice(parseInt(id));
      setTaxAdvice(advice);
      setShowTaxAdvice(true);
    } catch (error) {
      console.error('Error generating tax advice:', error);
      toast.error('Błąd podczas generowania porady podatkowej');
    } finally {
      setGeneratingTaxAdvice(false);
    }
  };

  const handleGenerateMarketResearch = async () => {
    if (!id) return;
    setGeneratingMarketResearch(true);
    try {
      const research = await crmApi.generateMarketResearch(parseInt(id));
      setMarketResearch(research);
      setShowMarketResearch(true);
    } catch (error) {
      console.error('Error generating market research:', error);
      toast.error('Błąd podczas generowania raportu rynkowego');
    } finally {
      setGeneratingMarketResearch(false);
    }
  };

  const handleGenerateBusinessProfileAI = async () => {
    if (!id) return;
    setGeneratingBusinessProfile(true);
    try {
      const response = await crmApi.generateBusinessProfileAI(parseInt(id));
      setAiBusinessProfile(response.profile);
      setShowBusinessProfileAI(true);
      toast.info('✅ AI wygenerowało profesjonalny profil biznesowy!');
    } catch (error) {
      console.error('Error generating business profile AI:', error);
      toast.error('Błąd podczas pobierania sugestii AI');
    } finally {
      setGeneratingBusinessProfile(false);
    }
  };

  return {
    generatingAI, comprehensiveAnalysis, showComprehensiveAnalysis, setShowComprehensiveAnalysis,
    optimizationReport, showOptimizationReport, setShowOptimizationReport, generatingOptimization,
    purchaseListReport, showPurchaseList, setShowPurchaseList, generatingPurchaseList,
    courseRecommendations, showCourseRecommendations, setShowCourseRecommendations,
    generatingCourseRecommendations,
    generatedCourseCertificate, showCourseCertificate, setShowCourseCertificate,
    generatingBusinessProfile, aiBusinessProfile, showBusinessProfileAI, setShowBusinessProfileAI,
    productAnalysis, showProductAnalysis, setShowProductAnalysis,
    productActionResult, showProductActionResult, setShowProductActionResult, generatingProductAction,
    financialForecast, showFinancialForecast, setShowFinancialForecast, generatingForecast,
    taxAdvice, showTaxAdvice, setShowTaxAdvice, generatingTaxAdvice,
    marketResearch, showMarketResearch, setShowMarketResearch, generatingMarketResearch,
    handleGenerateAI, handleGenerateOptimization, handleGeneratePurchaseList,
    handleRecommendCourses, handleGenerateCourseCertificate, handleGenerateBusinessProfileAI,
    handleGenerateProductAnalysis, handleProductAIAction,
    handleGenerateFinancialForecast, handleGenerateTaxAdvice, handleGenerateMarketResearch,
  };
}
